import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Avatar,
    CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useLanguageContext } from '../hooks/useLanguageContext';
import { multilingual } from '../multilingual';
import { getHousePeriodicConsumptions, getHouseBillingInfo } from '../api_calls/api_calls_houses';

const DAYS_SHORT_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Map API labels (English day names or date strings) to localized short day names, preserving order. */
const translateDayLabels = (labelList, chartDaysShort) => {
    if (!labelList?.length || !(chartDaysShort && chartDaysShort.length >= 7)) return labelList;
    const DAYS_FULL_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return labelList.map((l) => {
        const fullIdx = DAYS_FULL_EN.indexOf(l);
        if (fullIdx >= 0) return chartDaysShort[fullIdx];
        const shortIdx = DAYS_SHORT_EN.indexOf(l);
        if (shortIdx >= 0) return chartDaysShort[shortIdx];
        const d = new Date(l);
        if (!isNaN(d.getTime())) {
            const monBased = (d.getDay() + 6) % 7;
            return chartDaysShort[monBased];
        }
        return l;
    });
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ApartmentCardEnergy = ({ apartment, inModal = false, initialData = null, buildingId }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { language } = useLanguageContext();
    const dictionary = multilingual[language]?.apartmentCard ?? multilingual.gr.apartmentCard;
    const dashboardDict = multilingual[language]?.dashboard ?? multilingual.gr.dashboard;
    const chartDaysShort = dashboardDict?.chartDaysShort ?? DAYS_SHORT_EN;

    const [lastWeekData, setLastWeekData] = useState(null);
    const [billingData, setBillingData] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);

    useEffect(() => {
        if (initialData != null || !apartment?.id) return;
        let mounted = true;
        setDataLoading(true);
        Promise.all([
            getHousePeriodicConsumptions('lastweek', apartment.id),
            getHouseBillingInfo(apartment.id),
        ])
            .then(([periodic, billing]) => {
                if (!mounted) return;
                const consumption = Array.isArray(periodic?.consumption) ? periodic.consumption : [];
                const labels = Array.isArray(periodic?.labels) ? periodic.labels : [];
                const total = consumption.reduce((sum, v) => sum + (Number(v) || 0), 0);
                setLastWeekData({ consumption, labels, total });
                setBillingData(billing || null);
            })
            .catch(() => {
                if (!mounted) return;
                setLastWeekData(null);
                setBillingData(null);
            })
            .finally(() => {
                if (mounted) setDataLoading(false);
            });
        return () => { mounted = false; };
    }, [apartment.id, initialData]);

    const effectiveLastWeek = initialData?.lastWeekData ?? lastWeekData;
    const effectiveBilling = initialData?.billingData ?? billingData;
    const totalConsumption = effectiveLastWeek?.total ?? apartment.totalConsumption ?? 0;
    const costLast7DaysEur = effectiveBilling?.cost != null ? Number(effectiveBilling.cost).toFixed(2) : (totalConsumption * 0.16).toFixed(2);
    const lastDaysConsumption = effectiveLastWeek?.consumption?.length
        ? effectiveLastWeek.consumption
        : (apartment.lastDaysConsumption || [0, 0, 0, 0, 0, 0, 0]);
    const rawLabels = effectiveLastWeek?.labels?.length ? effectiveLastWeek.labels : (apartment.lastDaysLabels || DAYS_SHORT_EN);
    const lastDaysLabels = translateDayLabels(rawLabels, chartDaysShort);

    const option = {
        xAxis: {
            type: 'category',
            data: lastDaysLabels,
            axisLine: { show: true },
            axisLabel: { color: '#999', fontSize: 10 },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            axisLine: { show: false },
            axisLabel: { color: '#999', fontSize: 10 },
            splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
        },
        series: [
            {
                data: lastDaysConsumption,
                type: 'line',
                smooth: true,
                areaStyle: { color: '#2196f3', opacity: 0.3 },
                lineStyle: { color: '#2196f3', width: 2 },
                symbol: 'none',
            },
        ],
        grid: { top: 25, bottom: 35, left: 40, right: 10 },
        tooltip: {
            trigger: 'axis',
            formatter: '{b}<br/>{c} kWh',
        },
    };

    return (
        <Card
            elevation={0}
            onClick={() => navigate(`/building/${buildingId}/apartment/${apartment.id}`)}
            sx={{
                borderRadius: 5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'none',
                backgroundColor: 'white',
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                    borderColor: alpha(theme.palette.secondary.main, 0.5),
                },
            }}
        >
            <CardContent sx={{ padding: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: theme.palette.secondary.main }}>
                            <ApartmentIcon sx={{ color: theme.palette.common.white, fontSize: 20 }} />
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {apartment.name}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ mt: 2, flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            {dictionary?.last7DaysConsumption}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {(dataLoading && !initialData) && <CircularProgress size={14} sx={{ color: '#2196f3', mr: 0.5 }} />}
                            <Typography variant="subtitle2" sx={{ color: '#2196f3', mr: 1 }}>
                                {dictionary?.total}:
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="#2196f3" sx={{ lineHeight: 1 }}>
                                {totalConsumption.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#2196f3' }}>
                                kWh
                            </Typography>
                        </Box>
                    </Box>
                    {(dataLoading && !initialData && !lastWeekData) ? (
                        <Box sx={{ height: 150, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                            <CircularProgress size={28} sx={{ color: '#2196f3' }} />
                        </Box>
                    ) : (
                        <ReactECharts option={option} style={{ height: 150, width: '100%' }} />
                    )}
                </Box>

                {inModal ? (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{dictionary.estimatedCost}</Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.dark">€{costLast7DaysEur}</Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            mt: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            pt: 1.5,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {dictionary.checkIn}: <strong>{formatDate(apartment.checkIn)}</strong>
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventAvailableIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {dictionary.checkOut}: <strong>{formatDate(apartment.checkOut)}</strong>
                            </Typography>
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default ApartmentCardEnergy;
